import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import GridPage from "components/layout/GridPage";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import Select from "components/common/Select";
import TextField from "components/common/TextField";
import Loading from "components/common/Loading";
import {
  useListSystemSchedules,
  useCreateSystemSchedule,
  useUpdateSystemSchedule,
  useExecuteSystemSchedule,
} from "services/scheduling";
import {
  CreateSystemScheduleRequest,
  ExecuteSystemScheduleRequest,
  Schedule,
  UpdateSystemScheduleRequest,
} from "protogen/scheduling_service_pb";
import DateDisplay from "components/common/DateDisplay";
import useIsMobile from "components/hooks/useIsMobile";
import Checkbox from "components/common/Checkbox";
import SnackPack, { SnackbarMessage } from "components/common/SnackPack";
import { DateTimePicker } from "@mui/x-date-pickers";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

type ScheduleDialogProps = {
  open: boolean;
  schedule: Schedule | null;
  onClose: () => void;
  onSave: (data: {
    deadlineSec: bigint;
    payloadJson: string;
    recurringUnit: string;
    recurringInterval: number;
  }) => Promise<void>;
  onCancel?: () => void;
  onExecute?: () => void;
};

const RECURRING_UNITS = ["", "minute", "hour", "day", "week", "month", "year"];

const extractTitleFromPayload = (payloadJson: string): string => {
  try {
    const parsed = JSON.parse(payloadJson);
    return parsed.title || "";
  } catch {
    return "";
  }
};

const removeKeyFromPayload = (payloadJson: string, key: string): string => {
  try {
    const parsed = JSON.parse(payloadJson);
    delete parsed[key];
    return JSON.stringify(parsed, null, 2);
  } catch {
    return payloadJson;
  }
};

const ScheduleDialog = ({
  open,
  schedule,
  onClose,
  onSave,
  onCancel,
  onExecute,
}: ScheduleDialogProps) => {
  const [deadline, setDeadline] = useState<Date | null>(null);
  const [title, setTitle] = useState("");
  const [payloadJson, setPayloadJson] = useState("");
  const [recurringUnit, setRecurringUnit] = useState("");
  const [recurringInterval, setRecurringInterval] = useState(0);
  const [jsonError, setJsonError] = useState<string | null>(null);

  useEffect(() => {
    if (schedule) {
      setDeadline(new Date(Number(schedule.deadlineSec) * 1000));
      setTitle(extractTitleFromPayload(schedule.payloadJson));
      setPayloadJson(removeKeyFromPayload(schedule.payloadJson, "title"));
      setRecurringUnit(schedule.recurringUnit);
      setRecurringInterval(schedule.recurringInterval);
    } else {
      const oneHourFromNow = new Date();
      oneHourFromNow.setHours(oneHourFromNow.getHours() + 1);
      setDeadline(oneHourFromNow);
      setTitle("");
      setPayloadJson('{\n  "task_type": "",\n  "method": ""\n}');
      setRecurringUnit("");
      setRecurringInterval(0);
    }
    setJsonError(null);
  }, [schedule, open]);

  const validateJson = (json: string): boolean => {
    try {
      const parsed = JSON.parse(json);
      if (typeof parsed !== "object" || !parsed.task_type || !parsed.method) {
        setJsonError(
          "Payload must be a JSON object with a 'task_type' and 'method' field",
        );
        return false;
      }
      setJsonError(null);
      return true;
    } catch {
      setJsonError("Invalid JSON");
      return false;
    }
  };

  const handleSave = async () => {
    if (!deadline) return;
    if (!validateJson(payloadJson)) return;

    // Merge title into payload if provided
    let finalPayload = payloadJson;
    try {
      const parsed = JSON.parse(payloadJson);
      if (title.trim()) {
        parsed.title = title.trim();
      }
      finalPayload = JSON.stringify(parsed);
    } catch {
      // Keep original if parsing fails
    }

    await onSave({
      deadlineSec: BigInt(Math.floor(deadline.getTime() / 1000)),
      payloadJson: finalPayload,
      recurringUnit,
      recurringInterval,
    });
  };

  const isEditMode = !!schedule;
  const canCancel = isEditMode && schedule?.status === "upcoming" && onCancel;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEditMode ? "Edit Schedule" : "Create Schedule"}
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{ display: "flex", flexDirection: "column", gap: "16px", mt: 1 }}
        >
          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            placeholder="Optional display name for this schedule"
          />
          <DateTimePicker
            label="Deadline"
            value={deadline}
            onChange={(newValue) => setDeadline(newValue)}
            sx={{
              ".MuiInputAdornment-root": {
                marginRight: "12px",
              },
            }}
          />
          <Box sx={{ display: "flex", gap: "16px" }}>
            <FormControl fullWidth>
              <Select
                variant="outlined"
                value={recurringUnit}
                label="Recurring Unit"
                onChange={(e) => setRecurringUnit(e.target.value)}
              >
                {RECURRING_UNITS.map((unit) => (
                  <MenuItem key={unit} value={unit}>
                    {unit || "(none)"}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Recurring Interval"
              type="number"
              value={recurringInterval}
              onChange={(e) =>
                setRecurringInterval(parseInt(e.target.value) || 0)
              }
              disabled={!recurringUnit}
              fullWidth
            />
          </Box>

          <TextField
            label="Payload JSON"
            multiline
            rows={8}
            value={payloadJson}
            onChange={(e) => {
              setPayloadJson(e.target.value);
              validateJson(e.target.value);
            }}
            error={!!jsonError}
            helperText={
              jsonError ||
              "Must be a JSON object with a 'task_type' and 'method' field"
            }
            fullWidth
            InputProps={{
              sx: { fontFamily: "monospace", fontSize: "14px" },
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: "16px 24px" }}>
        <Box sx={{ display: "flex", gap: "16px", marginRight: "auto" }}>
          {canCancel && (
            <Button
              onClick={onCancel}
              color="error"
              variant="outlined"
              sx={{
                "&:hover": {
                  backgroundColor: "error.main",
                  color: "error.contrastText",
                },
              }}
            >
              Cancel Schedule
            </Button>
          )}
          {isEditMode && onExecute && (
            <Tooltip title="Run the task payload now, independent of the schedule">
              <Button
                onClick={onExecute}
                color="info"
                variant="text"
                startIcon={<PlayArrowIcon />}
              >
                Execute Now
              </Button>
            </Tooltip>
          )}
        </Box>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
        <Button onClick={handleSave} variant="contained">
          {isEditMode ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

type CancelDialogProps = {
  open: boolean;
  schedule: Schedule | null;
  onClose: () => void;
  onConfirm: () => void;
};

const CancelConfirmDialog = ({
  open,
  schedule,
  onClose,
  onConfirm,
}: CancelDialogProps) => {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Cancel Schedule</DialogTitle>
      <DialogContent>
        <Typography>
          Are you sure you want to cancel this scheduled task? This action
          cannot be undone.
        </Typography>
        {schedule && (
          <Box sx={{ mt: 2, p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
            <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
              {schedule.payloadJson}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          No, keep it
        </Button>
        <Button onClick={onConfirm} color="error" variant="contained">
          Yes, cancel it
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const getStatusChip = (status: string) => {
  const colorMap: Record<string, "success" | "warning" | "error" | "default"> =
    {
      upcoming: "success",
      executed: "default",
      canceled: "error",
    };
  return (
    <Chip
      label={status}
      size="small"
      color={colorMap[status] || "default"}
      variant="outlined"
    />
  );
};

const getPayloadPreview = (payloadJson: string): string => {
  try {
    const parsed = JSON.parse(payloadJson);
    return parsed.task_type || "(no task type)";
  } catch {
    return "(invalid json)";
  }
};

export default () => {
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const [includeCanceled, setIncludeCanceled] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<Schedule | null>(
    null,
  );
  const [snackPack, setSnackPack] = useState<readonly SnackbarMessage[]>([]);

  const { data, loading, request: listRequest } = useListSystemSchedules();

  const { request: createRequest } = useCreateSystemSchedule(() => {
    setSnackPack((p) => [
      ...p,
      {
        message: "Schedule created",
        saveNotification: true,
        key: Date.now().toString(),
      },
    ]);
    listRequest({ includeCanceled, taskType: "" });
  });

  const { request: executeRequest } = useExecuteSystemSchedule(() => {
    setSnackPack((p) => [
      ...p,
      {
        message: "Task submitted",
        saveNotification: true,
        key: Date.now().toString(),
      },
    ]);
  });

  const { request: updateRequest } = useUpdateSystemSchedule(() => {
    setSnackPack((p) => [
      ...p,
      {
        message: "Schedule updated",
        saveNotification: true,
        key: Date.now().toString(),
      },
    ]);
    listRequest({ includeCanceled, taskType: "" });
  });

  useEffect(() => {
    listRequest({ includeCanceled, taskType: "" });
  }, [includeCanceled]);

  // Open dialog from URL on page load
  useEffect(() => {
    const refFromUrl = searchParams.get("ref");
    if (refFromUrl && data?.schedules && !dialogOpen) {
      const schedule = data.schedules.find((s) => s.ref === refFromUrl);
      if (schedule) {
        setSelectedSchedule(schedule);
        setDialogOpen(true);
      } else if (refFromUrl === "new") {
        setSelectedSchedule(null);
        setDialogOpen(true);
      }
    }
  }, [data?.schedules, searchParams]);

  const updateUrlRef = (ref: string | null) => {
    if (ref) {
      setSearchParams({ ref });
    } else {
      setSearchParams({});
    }
  };

  const handleRowClick = (schedule: Schedule) => {
    setSelectedSchedule(schedule);
    setDialogOpen(true);
    updateUrlRef(schedule.ref);
  };

  const handleSave = async (saveData: {
    deadlineSec: bigint;
    payloadJson: string;
    recurringUnit: string;
    recurringInterval: number;
  }) => {
    let resp = null;
    if (selectedSchedule) {
      resp = await updateRequest(
        new UpdateSystemScheduleRequest({
          scheduleRef: selectedSchedule.ref,
          deadlineSec: saveData.deadlineSec,
          payloadJson: saveData.payloadJson,
          recurringUnit: saveData.recurringUnit,
          recurringInterval: saveData.recurringInterval,
        }),
      );
    } else {
      resp = await createRequest(
        new CreateSystemScheduleRequest({
          deadlineSec: saveData.deadlineSec,
          payloadJson: saveData.payloadJson,
          recurringUnit: saveData.recurringUnit,
          recurringInterval: saveData.recurringInterval,
        }),
      );
    }
    if (resp) {
      setSnackPack((p) => [
        ...p,
        {
          message: "Schedule updated",
          saveNotification: true,
          key: Date.now().toString(),
        },
      ]);
      setDialogOpen(false);
      setSelectedSchedule(null);
      updateUrlRef(null);
    }
  };

  const handleExecute = () => {
    if (selectedSchedule) {
      executeRequest(
        new ExecuteSystemScheduleRequest({
          scheduleRef: selectedSchedule.ref,
        }),
      );
    }
  };

  const handleCancelSchedule = () => {
    setDialogOpen(false);
    setCancelDialogOpen(true);
  };

  const handleConfirmCancel = () => {
    if (selectedSchedule) {
      updateRequest(
        new UpdateSystemScheduleRequest({
          scheduleRef: selectedSchedule.ref,
          status: "canceled",
        }),
      );
    }
    setCancelDialogOpen(false);
    setSelectedSchedule(null);
    updateUrlRef(null);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedSchedule(null);
    updateUrlRef(null);
  };

  const handleCancelDialogClose = () => {
    setCancelDialogOpen(false);
    setSelectedSchedule(null);
    updateUrlRef(null);
  };

  return (
    <GridPage
      sx={{
        padding: isMobile ? "32px 24px" : "48px",
        margin: isMobile ? "" : "auto",
        maxWidth: "1200px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: isMobile ? "12px" : "",
          justifyContent: "space-between",
          marginBottom: "16px",
        }}
      >
        <Typography variant="title">Schedule Management</Typography>
        <Box sx={{ display: "flex", gap: "16px", alignItems: "center" }}>
          <FormControlLabel
            label={
              <Typography variant="body" sx={{ WebkitUserSelect: "none" }}>
                Show canceled
              </Typography>
            }
            control={
              <Checkbox
                checked={includeCanceled}
                onChange={() => setIncludeCanceled((u) => !u)}
              />
            }
          />
          <Button
            variant="contained"
            onClick={() => {
              setSelectedSchedule(null);
              setDialogOpen(true);
              updateUrlRef("new");
            }}
          >
            Create Schedule
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table aria-label="schedule management table">
          <TableHead>
            <TableRow>
              <TableCell>Status</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Deadline</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Recurring</TableCell>
              <TableCell>Created</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading &&
              (data?.schedules || []).map((schedule) => (
                <TableRow
                  key={schedule.ref}
                  hover
                  onClick={() => handleRowClick(schedule)}
                  style={{ cursor: "pointer" }}
                >
                  <TableCell>{getStatusChip(schedule.status)}</TableCell>
                  <TableCell>
                    {extractTitleFromPayload(schedule.payloadJson) || "-"}
                  </TableCell>
                  <TableCell>
                    <DateDisplay
                      date={new Date(Number(schedule.deadlineSec) * 1000)}
                    />
                  </TableCell>
                  <TableCell>
                    {getPayloadPreview(schedule.payloadJson)}
                  </TableCell>
                  <TableCell>
                    {schedule.recurringUnit
                      ? `Every ${schedule.recurringInterval} ${schedule.recurringUnit}(s)`
                      : "-"}
                  </TableCell>
                  <TableCell>
                    <DateDisplay
                      date={new Date(Number(schedule.creationDateSec) * 1000)}
                    />
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      {loading && <Loading />}

      {!loading && (!data?.schedules || data.schedules.length === 0) && (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="body" color="text.secondary">
            No system schedules found.
          </Typography>
        </Box>
      )}

      <ScheduleDialog
        open={dialogOpen}
        schedule={selectedSchedule}
        onClose={handleDialogClose}
        onSave={handleSave}
        onExecute={selectedSchedule ? handleExecute : undefined}
        onCancel={
          selectedSchedule?.status === "upcoming"
            ? handleCancelSchedule
            : undefined
        }
      />

      <CancelConfirmDialog
        open={cancelDialogOpen}
        schedule={selectedSchedule}
        onClose={handleCancelDialogClose}
        onConfirm={handleConfirmCancel}
      />

      <SnackPack
        autoHideDuration={3000}
        snackPack={snackPack}
        alertSeverity="success"
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      />
    </GridPage>
  );
};
